import {
  createIdentitySession,
  publishIdentity,
  createRetentionProvider,
  toAyDimension,
} from '@redbrickmedia/duplex.user-identity'
import { OpenFeature } from '@openfeature/web-sdk'
import { useAnalytics } from '../composables/analytics/useAnalytics'
import { preAYScripts } from '../utils/ad-ops/preAYScripts'
import {
  resolveConsent,
  type ConsentVerdict,
} from '../utils/consent/consentGate'
import debug from '../utils/console'

const logger = debug.withTag('plugins:user-identity')

/** AY custom dimension naming the provider that resolved the HEM. */
const HEM_PROVIDER_DIMENSION = 'custom_13'

/**
 * Run `fn` once Assertive Yield is up.
 *
 * Waiting for consent means this can now be called either side of the AY
 * script load, and `assertiveQueue` only covers the "before" case — it is a
 * plain array the vendor drains on load. Once AY is up the queue may never be
 * read again, so call straight through instead of pushing into it.
 */
const afterAy = (fn: () => void) => {
  if (window.assertive) {
    fn()
    return
  }
  window.assertiveQueue = window.assertiveQueue || []
  window.assertiveQueue.push(fn)
}

export default defineNuxtPlugin({
  name: 'user-identity',
  dependsOn: ['openfeature', 'user-segment'],
  setup: async () => {
    // Both of these are unconditional and deliberately ahead of the
    // `enableUserIdentity` check below: the ad and Google Consent Mode gates
    // read the same verdict, and neither is scoped to the identity
    // integration. They also run before the first `await` so the clock starts
    // as early as this plugin can start it.
    //
    // `preAYScripts()` is what loads the CMP the verdict waits on. `assertive`
    // awaits the same call later, but the script loader memoizes by src, so it
    // receives this in-flight promise and AY still bootstraps strictly after
    // it. Requesting the CMP here rather than there buys back most of the
    // consent budget.
    void preAYScripts()
    void resolveConsent(useNuxtApp().$filters?.country)

    const { client } = await useOpenFeature()
    if (!client.getBooleanValue('enableUserIdentity', false)) {
      return
    }

    const { targetingKey } = OpenFeature.getContext()
    if (typeof targetingKey !== 'string' || !targetingKey) {
      logger.warn('No targetingKey available; skipping identity capture')
      return
    }

    const { track } = useAnalytics()

    const start = (verdict: ConsentVerdict) => {
      // Assertive Yield depends on this plugin, so nothing here may throw —
      // an identity failure must never stop ads from loading.
      let session
      try {
        // No `source`: the EID source has to be a real domain buyers have a
        // mapping for, so it is left to the package to take from the page.
        // Which tenant it was travels in the AY dimensions instead.
        session = createIdentitySession({
          userId: targetingKey,
          hasConsent: verdict.granted,
          // Built only once consent is granted. The Retention provider loads
          // ge.js the moment it is constructed, so building it as part of the
          // argument literal would fire the vendor request regardless of the
          // verdict — `hasConsent: false` cannot call it back.
          provider: verdict.granted
            ? createRetentionProvider({
                onEvent: (event) =>
                  event.type === 'script:load'
                    ? track('NtpRetention_ScriptLoaded', {
                        success: event.success,
                      })
                    : track('NtpRetention_MatchResult', {
                        hem_resolved: event.hemResolved,
                        status: event.status,
                      }),
              })
            : undefined,
        })
      } catch (error) {
        logger.warn('Failed to start identity session', error)
        return
      }

      if (!verdict.granted) {
        // The session was still built: `hasConsent: false` clears any HEM
        // stored from an earlier pageview, which is the part that matters for
        // a visitor who has since opted out. Nothing is published.
        logger.info(`Identity declined (basis: ${verdict.basis})`)
        return
      }

      // Account key and user-id submodules are the same across every property,
      // so both come from the package rather than app config. Publishing is
      // idempotent — modules are keyed by name — so the late-HEM pass replaces
      // our pubProvidedId entry rather than duplicating the list.
      const publish = () => {
        try {
          publishIdentity(session, { sendToGAM: false })
        } catch {
          logger.warn('Failed to publish identity to Prebid')
        }
      }

      // Written into the live analytics object rather than passed at init: the
      // HEM usually resolves after scriptLoadAY has already built `custom`, and
      // the value has to be replaced in place when it does. Reads 'UnResolved'
      // until then.
      const syncAyHemProvider = () => {
        const patch = toAyDimension(session.current, {
          dimension: HEM_PROVIDER_DIMENSION,
        })
        afterAy(() => {
          if (window.assertive?.analytics?.custom) {
            Object.assign(window.assertive.analytics.custom, patch)
          }
        })
      }

      // Publishing has to happen after AY: it applies its Prebid config once on
      // script load, so anything written before that is overwritten.
      afterAy(publish)
      syncAyHemProvider()

      // A HEM landing before AY loads would publish into config AY then
      // discards, but the queued pass above re-publishes it afterwards, so
      // either order converges.
      session.onHem(() => {
        publish()
        syncAyHemProvider()
      })
    }

    // Deliberately not awaited. `assertive` depends on this plugin and is what
    // awaits the CMP, so blocking on the verdict here would stall the chain on
    // a script that cannot finish loading until this returns. The read itself
    // was kicked off at the top of this function.
    //
    // The cost is that resolution no longer overlaps the AY script load — the
    // HEM lookup now starts once consent settles, which is usually after AY is
    // up. The queued publish and `onHem` re-publish both handle that order.
    void resolveConsent()
      .then(start)
      .catch((error) => logger.warn('Consent gate failed', error))
  },
})
